/**
 * Фабрика для создания RTK Query эндпоинтов
 * Упрощает создание стандартных query и mutation эндпоинтов
 */

export type ApiMethod<TArgs = any, TResponse = any> = (
	...args: TArgs[]
) => Promise<TResponse>;

/**
 * Создаёт конфигурацию query эндпоинта
 * @param apiMethod - метод API для вызова
 * @param tags - теги для кэша (providesTags)
 * @returns объект конфигурации query
 * 
 * @example
 * getUserInfo: builder.query<UserInfoResponse, void>({
 *   ...createQueryEndpoint(userAPI.getUserInfo, ['userInfo']),
 * }),
 */
export const createQueryEndpoint = <TResponse = any, TRequest = any>(
	apiMethod: ApiMethod,
	tags: readonly string[] = [],
) => ({
	query: (data?: TRequest) => ({
		func: apiMethod,
		args: data !== undefined ? [data] : [],
	}),
	providesTags: tags as any,
});

/**
 * Создаёт конфигурацию mutation эндпоинта
 * @param apiMethod - метод API для вызова
 * @param invalidatesTags - теги для инвалидации кэша
 * @returns объект конфигурации mutation
 * 
 * @example
 * editUserInfo: builder.mutation<BaseResponseType, UserEditData>({
 *   ...createMutationEndpoint(userAPI.editUserInfo, ['userInfo']),
 * }),
 */
export const createMutationEndpoint = <TResponse = any, TRequest = any>(
	apiMethod: ApiMethod,
	invalidatesTags: readonly string[] = [],
) => ({
	query: (data: TRequest) => ({
		func: apiMethod,
		args: [data],
	}),
	invalidatesTags: invalidatesTags as any,
});

/**
 * Создаёт конфигурацию query эндпоинта с обработчиками
 * @param apiMethod - метод API для вызова
 * @param tags - теги для кэша
 * @param transformResponse - функция трансформации успешного ответа
 * @param transformErrorResponse - функция трансформации ошибки
 * @returns объект конфигурации query с обработчиками
 * 
 * @example
 * getStudyStatus: builder.query<StudyStatusResponse, void>({
 *   ...createQueryEndpointWithHandlers(
 *     userAPI.getStudyStatus,
 *     ['studyStatus'],
 *     undefined,
 *     (error) => { console.error('Study status error:', error); return error; }
 *   ),
 * }),
 */
export const createQueryEndpointWithHandlers = <TResponse = any, TRequest = any>(
	apiMethod: ApiMethod,
	tags: readonly string[] = [],
	transformResponse?: (response: any) => TResponse,
	transformErrorResponse?: (error: any) => any,
) => {
	const config: any = {
		query: (data?: TRequest) => ({
			func: apiMethod,
			args: data !== undefined ? [data] : [],
		}),
		providesTags: tags as any,
	};

	if (transformResponse) {
		config.transformResponse = transformResponse;
	}

	if (transformErrorResponse) {
		config.transformErrorResponse = transformErrorResponse;
	}

	return config;
};

/**
 * Создаёт конфигурацию mutation эндпоинта с обработчиками
 * @param apiMethod - метод API для вызова
 * @param invalidatesTags - теги для инвалидации кэша
 * @param transformResponse - функция трансформации успешного ответа
 * @param transformErrorResponse - функция трансформации ошибки
 * @returns объект конфигурации mutation с обработчиками
 * 
 * @example
 * generateCode: builder.mutation<GenerateCodeResponse, GenerateCodeData>({
 *   ...createMutationEndpointWithHandlers(
 *     clientsAPI.sendSms,
 *     [],
 *     (response) => {
 *       if (response.approve) {
 *         localStorage.setItem('choiceRegClient', JSON.stringify(response));
 *       }
 *       return response;
 *     }
 *   ),
 * }),
 */
export const createMutationEndpointWithHandlers = <
	TResponse = any,
	TRequest = any,
>(
	apiMethod: ApiMethod,
	invalidatesTags: readonly string[] = [],
	transformResponse?: (response: any) => TResponse,
	transformErrorResponse?: (error: any) => any,
) => {
	const config: any = {
		query: (data: TRequest) => ({
			func: apiMethod,
			args: [data],
		}),
		invalidatesTags: invalidatesTags as any,
	};

	if (transformResponse) {
		config.transformResponse = transformResponse;
	}

	if (transformErrorResponse) {
		config.transformErrorResponse = transformErrorResponse;
	}

	return config;
};

/**
 * Создаёт простой query эндпоинт без аргументов
 * @param apiMethod - метод API для вызова
 * @param tags - теги для кэша
 * @returns объект конфигурации query без аргументов
 * 
 * @example
 * getCart: builder.query<CartResponse, void>({
 *   ...createSimpleQueryEndpoint(userAPI.getCartInfo, ['cart']),
 * }),
 */
export const createSimpleQueryEndpoint = <TResponse = any>(
	apiMethod: ApiMethod,
	tags: readonly string[] = [],
) => ({
	query: () => ({
		func: apiMethod,
		args: [],
	}),
	providesTags: tags as any,
});

